// /pill-identify camera-driven pill identifier with optional
// front + back photos. The user snaps the front, optionally snaps
// the back, then taps Identify. We show the extracted visual
// features so the user can verify what Siggy saw before trusting
// any candidate.

import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ComplianceNotice } from '../components/ComplianceNotice';
import { SiggyAvatar } from '../components/SiggyAvatar';
import { EmptyIllustration } from '../components/senior/EmptyIllustration';
import { SeniorButton } from '../components/senior/SeniorButton';
import { env } from '../env';
import { useSession } from '../hooks/SessionProvider';
import { AppPage } from '../layouts/AppPage';

interface PillCandidate {
  name: string;
  strength: string | null;
  imprint: string | null;
  shape: string | null;
  color: string | null;
  confidence: 'high' | 'medium' | 'low';
  reasoning: string;
}

interface PillFeatures {
  shape: string | null;
  color: string | null;
  imprintFront: string | null;
  imprintBack: string | null;
  size: string | null;
  score: string | null;
  notes: string | null;
}

interface ImprintMatch {
  ndc: string;
  brandName: string;
  genericName: string;
  strength: string | null;
  shape: string | null;
  color: string | null;
  imprintMatched: string;
  score: number;
}

interface PillResult {
  unreadable: boolean;
  features: PillFeatures;
  candidates: PillCandidate[];
  imprintMatches?: ImprintMatch[];
}

const MAX_DIM = 1600;

export function PillIdentify(): JSX.Element {
  const { sessionToken } = useSession();
  const frontInputRef = useRef<HTMLInputElement>(null);
  const backInputRef = useRef<HTMLInputElement>(null);
  const [frontUrl, setFrontUrl] = useState<string | null>(null);
  const [backUrl, setBackUrl] = useState<string | null>(null);
  const [frontBlob, setFrontBlob] = useState<Blob | null>(null);
  const [backBlob, setBackBlob] = useState<Blob | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState<PillResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function pick(file: File, side: 'front' | 'back') {
    const previewUrl = URL.createObjectURL(file);
    const blob = await resize(file, MAX_DIM);
    if (side === 'front') {
      setFrontUrl(previewUrl);
      setFrontBlob(blob);
    } else {
      setBackUrl(previewUrl);
      setBackBlob(blob);
    }
    setResult(null);
    setError(null);
  }

  async function identify() {
    if (!frontBlob || analyzing) return;
    setAnalyzing(true);
    setError(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('front', new File([frontBlob], 'front.jpg', { type: 'image/jpeg' }));
      if (backBlob) {
        fd.append('back', new File([backBlob], 'back.jpg', { type: 'image/jpeg' }));
      }
      const res = await fetch(`${env.apiBaseUrl}/api/pill-identify/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${sessionToken ?? ''}` },
        body: fd,
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`HTTP ${res.status} ${text.slice(0, 200)}`);
      }
      const json = (await res.json()) as {
        ok: boolean;
        data?: PillResult;
        error?: { message: string };
      };
      if (!json.ok || !json.data) {
        throw new Error(json.error?.message ?? 'Could not identify');
      }
      setResult(json.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setAnalyzing(false);
    }
  }

  const reset = () => {
    setFrontUrl(null);
    setBackUrl(null);
    setFrontBlob(null);
    setBackBlob(null);
    setResult(null);
    setError(null);
  };

  return (
    <AppPage title="Identify a pill" back={{ to: '/medications', label: 'Medicines' }}>
      <ComplianceNotice
        variant="clinical"
        detail="Pill identification is a best-guess match, not a diagnostic. Verify with the prescribing pharmacy before taking anything you cannot confirm."
      />
      <section className="food-intro">
        <SiggyAvatar size={48} />
        <div>
          <p className="food-intro__eyebrow">Siggy says</p>
          <p className="food-intro__text">
            Snap the <strong>front and back</strong> of the pill on a plain background with good
            light. The imprint code (the letters or numbers) is what I use to identify it the
            clearer the photo, the better my guess.
          </p>
        </div>
      </section>

      <section className="account-section">
        <h3 className="account-section__title">Photos</h3>
        <div className="pill-photo-grid">
          <PhotoSlot
            label="Front of pill"
            previewUrl={frontUrl}
            onClick={() => frontInputRef.current?.click()}
            required
          />
          <PhotoSlot
            label="Back of pill (optional but recommended)"
            previewUrl={backUrl}
            onClick={() => backInputRef.current?.click()}
          />
        </div>
        <input
          ref={frontInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void pick(f, 'front');
          }}
          style={{ display: 'none' }}
        />
        <input
          ref={backInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void pick(f, 'back');
          }}
          style={{ display: 'none' }}
        />

        <div style={{ display: 'flex', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
          <SeniorButton
            variant="primary"
            onClick={() => void identify()}
            disabled={!frontBlob || analyzing}
          >
            {analyzing
              ? 'Reading the pill...'
              : backBlob
                ? 'Identify (front + back)'
                : 'Identify (front only)'}
          </SeniorButton>
          {(frontBlob || backBlob) && !analyzing ? (
            <button type="button" className="ghost-cta" onClick={reset}>
              Start over
            </button>
          ) : null}
        </div>
      </section>

      {error ? (
        <div className="account-section">
          <p style={{ color: 'var(--color-danger)' }}>{error}</p>
        </div>
      ) : null}

      {result ? <PillResultView result={result} /> : null}

      <section className="account-section">
        <p style={{ fontSize: 16, color: 'var(--color-text-secondary)', margin: 0 }}>
          This is an educational identification only. Pill identification by photo is hard, even for
          trained pharmacists. Before you take any pill you are not sure about, call your
          pharmacist. They can confirm by imprint code in seconds, often for free.
        </p>
      </section>
    </AppPage>
  );
}

function PhotoSlot({
  label,
  previewUrl,
  onClick,
  required,
}: {
  label: string;
  previewUrl: string | null;
  onClick: () => void;
  required?: boolean;
}): JSX.Element {
  return (
    <button type="button" className="pill-photo-slot" onClick={onClick}>
      {previewUrl ? (
        <img src={previewUrl} alt={label} className="pill-photo-slot__img" />
      ) : (
        <div className="pill-photo-slot__placeholder">
          <svg width="36" height="36" viewBox="0 0 24 24" aria-hidden="true">
            <title>Camera</title>
            <path
              d="M4 7h3l2-3h6l2 3h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1z"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
            <circle cx="12" cy="13" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
          </svg>
          <span>{label}</span>
          {required ? <span className="pill-photo-slot__required">Required</span> : null}
        </div>
      )}
    </button>
  );
}

function PillResultView({ result }: { result: PillResult }): JSX.Element {
  if (result.unreadable) {
    return (
      <EmptyIllustration
        kind="pill-identify"
        title="I could not read the pill clearly"
        body="Try again with brighter lighting and the imprint code (the letters or numbers) facing the camera. A plain white background like a piece of paper helps a lot."
      />
    );
  }

  const imprintMatches = result.imprintMatches ?? [];
  return (
    <>
      <section className="account-section">
        <h3 className="account-section__title">What I see in the photo</h3>
        <p style={{ fontSize: 16, color: 'var(--color-text-secondary)', margin: '0 0 12px' }}>
          Before I name the pill, here is what I read off the photo. If any of this is wrong, retake
          the photo and try again.
        </p>
        <dl className="pill-features">
          <FeatureRow label="Imprint (front)" value={result.features.imprintFront} />
          <FeatureRow label="Imprint (back)" value={result.features.imprintBack} />
          <FeatureRow label="Shape" value={result.features.shape} />
          <FeatureRow label="Color" value={result.features.color} />
          <FeatureRow label="Score line" value={result.features.score} />
          <FeatureRow label="Size" value={result.features.size} />
        </dl>
      </section>

      {imprintMatches.length > 0 ? (
        <section className="account-section">
          <h3 className="account-section__title">
            Pharmacy database matches{' '}
            <span className="confidence-pill confidence-pill--high" style={{ marginLeft: 8 }}>
              imprint match
            </span>
          </h3>
          <p style={{ fontSize: 16, color: 'var(--color-text-secondary)', margin: '0 0 12px' }}>
            These come from the NLM / OpenFDA imprint database, not Siggy's best guess. Exact
            imprint matches are the most reliable identification.
          </p>
          <ul className="pill-list">
            {imprintMatches.map((m) => (
              <li key={m.ndc} className="pill-card">
                <div className="pill-card__head">
                  <h4 className="pill-card__name">
                    {m.brandName}
                    {m.genericName && m.genericName !== m.brandName ? (
                      <span style={{ fontWeight: 400, color: 'var(--color-text-secondary)' }}>
                        {' '}
                        ({m.genericName})
                      </span>
                    ) : null}
                  </h4>
                  <span className="confidence-pill confidence-pill--high">{m.score}%</span>
                </div>
                {m.strength ? <p className="pill-card__strength">{m.strength}</p> : null}
                <p className="pill-card__reason">
                  Imprint <strong>{m.imprintMatched}</strong>
                  {m.shape ? <> · {m.shape}</> : null}
                  {m.color ? <> · {m.color}</> : null} · NDC {m.ndc}
                </p>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {result.candidates.length === 0 ? (
        <div className="empty-state">
          <h3 className="empty-state__title">No candidates found</h3>
          <p className="empty-state__body">
            I could see the pill but I cannot confidently name it from these photos. A clearer
            imprint photo would help. Or call your pharmacist they can identify by imprint in
            seconds.
          </p>
        </div>
      ) : (
        <section className="account-section">
          <h3 className="account-section__title">My best guesses</h3>
          <ul className="pill-list">
            {result.candidates.map((c, idx) => (
              <li key={`${c.name}-${idx}`} className="pill-card">
                <div className="pill-card__head">
                  <h4 className="pill-card__name">{c.name}</h4>
                  <span className={`confidence-pill confidence-pill--${c.confidence}`}>
                    {c.confidence}
                  </span>
                </div>
                {c.strength ? <p className="pill-card__strength">{c.strength}</p> : null}
                <p className="pill-card__reason">{c.reasoning}</p>
              </li>
            ))}
          </ul>
          <p style={{ fontSize: 16, marginTop: 12, color: 'var(--color-text-secondary)' }}>
            Want to add this to your list?{' '}
            <Link to="/medications/add-by-photo">Snap the bottle</Link> instead that reads the label
            and links to a real pharmacy database, which is more reliable than identifying a loose
            pill.
          </p>
        </section>
      )}
    </>
  );
}

function FeatureRow({ label, value }: { label: string; value: string | null }): JSX.Element {
  return (
    <>
      <dt>{label}</dt>
      <dd>{value ?? <span style={{ color: 'var(--color-text-tertiary)' }}>Not detected</span>}</dd>
    </>
  );
}

async function resize(file: File, maxDim: number): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D not supported');
  ctx.drawImage(bitmap, 0, 0, w, h);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Could not encode image'))),
      'image/jpeg',
      0.9,
    );
  });
}
